import {Injectable} from '@angular/core';

import { JsonHttpService } from '../../shared/http/json-http.service'
import { PartnerConversation, PartnerOnboarding, PartnerOnboardingComment, CredentialsStatus, TransportType } from '../models/partner-onboarding';
import { HttpParams } from '@angular/common/http';
import { lastValueFrom, map, Observable } from 'rxjs';
import { OnboardingCredentials } from '../components/onboarding-credentials/models/credentials';
import { CompanyProfile } from 'src/app/configuration/models/company-profile';

@Injectable({
    providedIn: 'root'
})
export class PartnerPortalService {

    constructor(private httpService: JsonHttpService) { }

    async getOnboardingPartner(partnerOnboardingGuid: string, partnerId: number): Promise<PartnerOnboarding> {
        const options = { params: new HttpParams() };
        if (partnerOnboardingGuid) options.params = options.params.append('partnerOnboardingGuid', `${partnerOnboardingGuid}`);
        if (partnerId) options.params = options.params.append('partnerId', `${partnerId}`);
        const onboardingPartner = this.httpService.getWithoutAccessToken('/api/partnerportal/get-partneronboarding', options) as Observable<PartnerOnboarding>;

        return lastValueFrom(onboardingPartner.pipe(
            map(result => {
                this.convertAllDateProperties(result);
                return result;
            })
        ));
    }

    async saveOnboardingPartner(partnerOnboarding: PartnerOnboarding, markAsDone: boolean): Promise<PartnerOnboarding> {
        const options = { params: new HttpParams().set('markAsDone', `${markAsDone}`) };
        const savedPartnerOnboarding = this.httpService.postWithoutAccessToken('/api/partnerportal/save-partneronboarding', partnerOnboarding, options) as Observable<PartnerOnboarding>;
        return await lastValueFrom(savedPartnerOnboarding);
    }

    async logAccess(partnerOnboardingGuid: string, isLoggedIn: boolean) {
        const options = { params: new HttpParams().set('partnerOnboardingGuid', `${partnerOnboardingGuid}`) };
        options.params = options.params.append('isLoggedIn', `${isLoggedIn}`);
        await this.httpService.postWithoutAccessToken('/api/partnerportal/log-access', null, options).toPromise();
    }

    async postComment(comment: PartnerOnboardingComment, conversationId: string, partnerOnboardingGuid: string): Promise<PartnerConversation> {
        const options = { params: new HttpParams().set('conversationId', `${conversationId}`) };
        options.params = options.params.append('partnerOnboardingGuid', `${partnerOnboardingGuid}`);
        const conversation = this.httpService.postWithoutAccessToken('/api/partnerportal/post-comment', comment, options) as Observable<PartnerConversation>;
        return await lastValueFrom(conversation);
    }

    async getConversations(partnerOnboardingGuid: string): Promise<PartnerConversation[]> {
        const options = { params: new HttpParams().set('partnerOnboardingGuid', `${partnerOnboardingGuid}`) };
        const conversations = this.httpService.getWithoutAccessToken('/api/partnerportal/get-conversations', options) as Observable<PartnerConversation[]>;
        return lastValueFrom(conversations.pipe(
            map(result => {
                this.convertAllDateProperties(result);
                return result;
            })
        ));
    }

    async setConversationReadByPartner(conversationId: string): Promise<void> {
        const options = { params: new HttpParams().set('conversationId', `${conversationId}`) };
        const conversation = this.httpService.postWithoutAccessToken('/api/partnerportal/set-conversation-read', null, options) as Observable<void>;
        return await lastValueFrom(conversation);
    }

    async requestCredentials(partnerOnboardingGuid: string, transportType: TransportType): Promise<CredentialsStatus> {
        const options = { params: new HttpParams().set('partnerOnboardingGuid', `${partnerOnboardingGuid}`) };
        options.params = options.params.append('transportType', `${transportType}`);
        const credentials = this.httpService.getWithoutAccessToken('/api/partnerportal/request-credentials', options) as Observable<CredentialsStatus>;
        return lastValueFrom(credentials.pipe(
            map(result => {
                this.convertAllDateProperties(result);
                return result;
            })
        ));
    }

    getGetCompanyProfile(partnerOnboardingGuid: string): Promise<CompanyProfile> {
        const options = { params: new HttpParams().set('partnerOnboardingGuid', `${partnerOnboardingGuid}`) };
        const observable = this.httpService.getWithoutAccessToken('/api/partnerportal/get-company-profile', options) as Observable<CompanyProfile>;
        return lastValueFrom(observable);
    }

    async getCredentials(partnerOnboardingGuid: string, transportType: TransportType): Promise<OnboardingCredentials> {
        const options = { params: new HttpParams().set('partnerOnboardingGuid', `${partnerOnboardingGuid}`) };
        options.params = options.params.append('transportType', `${transportType}`);
        const credentials = this.httpService.getWithoutAccessToken('/api/partnerportal/get-credentials', options) as Observable<OnboardingCredentials>;
        return lastValueFrom(credentials.pipe(
            map(result => {
                this.convertAllDateProperties(result);
                return result;
            })
        ));
    }


    private convertAllDateProperties(node: unknown): void {
        if (!node) return;

        if (Array.isArray(node)) {
            for (const item of node) this.convertAllDateProperties(item);
            return;
        }

        if (typeof node === 'object') {
            for (const key of Object.keys(node)) {
                const val = (node as Record<string, unknown>)[key];
                if (val === null) continue;

                if (typeof val === 'string' && this.isIsoDateString(val)) {  
                    node[key] = new Date(val);
                } else if (typeof val === 'object') {
                    this.convertAllDateProperties(val);
                }
            }
        }
    }

    private isIsoDateString(value: string): boolean {
        const isoDateTimeRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d+)?(Z|[+-][01]\d:[0-5]\d)?$/;
        if (!isoDateTimeRegex.test(value)) return false;
        return true;
    }
    
}
